import React, { useState, useEffect, useContext } from 'react';
import { Tooltip, IconButton, TextField, Box, Typography, Snackbar, Alert } from '@mui/material';
import axios from 'axios';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { ThemeContext } from "../context/ThemeContext";

const CreateFolderForm = ({ onCreateFolder, disableCreate }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);
  const [myPermissions, setMyPermissions] = useState({ can_create_folder: false });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const { mode, customColors } = useContext(ThemeContext);
  const apiUrl = process.env.REACT_APP_BASE_URL;

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    const fetchMyPermissions = async () => {
      try {
        const token = sessionStorage.getItem("authToken");
        const response = await axios.get(`${apiUrl}/user/permissions/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const { can_create_folder } = response.data.permissions || {};
        setMyPermissions({
          can_create_folder: can_create_folder || false,
          is_admin: false,
        });
      } catch (error) {
        console.error("Failed to fetch permissions:", error);
        showSnackbar("Failed to fetch permissions. Please try again.", "error");
        setMyPermissions({
          can_create_folder: false,
          is_admin: false,
        });
      }
    };
    const userRole = sessionStorage.getItem("userRole");
    if (userRole === "admin") {
      setMyPermissions({
        can_create_folder: true,
        is_admin: true,
      });
    } else {
      fetchMyPermissions();
    }
  }, [apiUrl]);

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (name.trim() === '') return;
  if (disableCreate || !myPermissions.can_create_folder) {
    setShowTooltip(true);
    showSnackbar(
      !myPermissions.can_create_folder
        ? "You do not have permission to create a folder."
        : "Cannot create folders inside a subfolder.",
      "warning"
    );
    return;
  }
  setShowTooltip(false);
  try {
    await onCreateFolder({ name, type }); // <-- Await async operation
    showSnackbar("Folder created successfully!", "success");
    setName('');
    setType('');
  } catch (error) {
    showSnackbar("Failed to create folder. Please try again.", "error");
  }
};


  const handleCancel = () => {
    setType('');
    setName('');
    setShowTooltip(false);
  };

  return (
    <Box sx={{ width: '100%', mb: 0.5 }}>
      {/* Create Folder Button (More Prominent) */}
     <Tooltip
  title={myPermissions.can_create_folder ? "Create New Folder" : "No permission"}
  arrow
  placement="right"
>
  <Box
    onClick={() => myPermissions.can_create_folder && setType('folder')}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      px: 2,
      py: 1.2,
      cursor: myPermissions.can_create_folder ? 'pointer' : 'not-allowed',
      borderRadius: 2,
      transition: 'all 0.2s ease',
      background: type === 'folder'
        ? "linear-gradient(135deg, #1e40af 0%, #1e8bafff 100%)"
        : mode === "dark"
        ? "rgba(30, 139, 175, 0.1)"
        : mode === "custom"
        ? `${customColors.background}30`
        : "rgba(30, 58, 138, 0.1)",
      boxShadow: type === 'folder'
        ? "0 2px 4px rgba(30, 58, 138, 0.3)"
        : "none",
      opacity: myPermissions.can_create_folder ? 1 : 0.6,
      border: type === 'folder'
        ? "none"
        : mode === "dark"
        ? "1px solid rgba(30, 139, 175, 0.2)"
        : mode === "custom"
        ? `1px solid ${customColors.border}`
        : "1px solid rgba(30, 58, 138, 0.2)",
      '&:hover': myPermissions.can_create_folder ? {
        background: type === 'folder'
          ? "linear-gradient(135deg, #1e40af 0%, #1e9ec5 100%)"
          : mode === "dark"
          ? "rgba(30, 139, 175, 0.2)"
          : mode === "custom"
          ? `${customColors.background}40`
          : "rgba(30, 58, 138, 0.15)",
        boxShadow: type === 'folder'
          ? "0 4px 8px rgba(30, 58, 138, 0.3)"
          : "0 2px 4px rgba(0, 0, 0, 0.1)",
      } : {},
    }}
  >
    <CreateNewFolderIcon
      sx={{
        fontSize: 28,
        color: type === 'folder'
          ? '#fff'
          : mode === "dark"
          ? "#90caf9"
          : mode === "custom"
          ? customColors.text
          : "#1e3a8a",
        transition: 'all 0.2s ease',
      }}
    />
    <Typography
      variant="body1"
      sx={{
        fontWeight: 600,
        fontSize: '1rem',
        color: type === 'folder'
          ? '#fff'
          : mode === "dark"
          ? "#fff"
          : mode === "custom"
          ? customColors.text
          : "#1e3a8a",
        transition: 'all 0.2s ease',
      }}
    >
      Create Folder
    </Typography>
  </Box>
</Tooltip>


      {/* Inline Form (TextField + Buttons in One Line) */}
  {type && (
  <Box
    component="form"
    onSubmit={handleSubmit}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 1.5,
      py: 1,
      background: mode === "dark"
        ? "rgba(30, 139, 175, 0.05)"
        : mode === "custom"
        ? `${customColors.background}20`
        : "rgba(30, 58, 138, 0.03)",
      borderLeft: "3px solid #1e8bafff",
      borderRadius: 1,
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    }}
  >
    {/* TextField (Compact & Button-Like) */}
    <TextField
      value={name}
      onChange={(e) => setName(e.target.value)}
      required
      variant="outlined"
      size="small"
      autoFocus
      placeholder="Folder name..."
      InputProps={{
        sx: {
          fontSize: "0.85rem",
          padding: "4px 8px",
          background: mode === "dark"
            ? "rgba(255, 255, 255, 0.05)"
            : mode === "custom"
            ? `${customColors.background}40`
            : "rgba(30, 58, 138, 0.05)",
          borderRadius: 1,
          "& fieldset": {
            borderColor: mode === "dark"
              ? "rgba(30, 139, 175, 0.3)"
              : "rgba(30, 58, 138, 0.2)",
          },
          "&:hover fieldset": {
            borderColor: "#1e8bafff",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#1e8bafff",
            borderWidth: "1px",
          },
        },
      }}
      sx={{
        flex: 1,
        "& .MuiInputBase-root": {
          height: 36,
        },
      }}
    />

    {/* Action Buttons (Button-Like) */}
    <Box display="flex" gap={0.5}>
      <Tooltip title="Create" arrow>
        <IconButton
          type="submit"
          size="small"
          sx={{
            background: "linear-gradient(135deg, #1e40af 0%, #1e8bafff 100%)",
            color: "#fff",
            width: 32,
            height: 32,
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
            '&:hover': {
              background: "linear-gradient(135deg, #1e40af 0%, #1e9ec5 100%)",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
            },
          }}
        >
          <CheckIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Cancel" arrow>
        <IconButton
          type="button"
          size="small"
          onClick={handleCancel}
          sx={{
            background: mode === "dark"
              ? "rgba(255, 255, 255, 0.05)"
              : mode === "custom"
              ? `${customColors.background}40`
              : "rgba(30, 58, 138, 0.05)",
            border: `1px solid ${mode === "dark" ? "rgba(30, 139, 175, 0.3)" : "rgba(30, 58, 138, 0.2)"}`,
            color: mode === "dark" ? "#1e8bafff" : "#1e3a8a",
            width: 32,
            height: 32,
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
            '&:hover': {
              background: mode === "dark"
                ? "rgba(30, 139, 175, 0.1)"
                : "rgba(30, 58, 138, 0.1)",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  </Box>
)}


      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateFolderForm;
